// Audit history panel component
// Shows change history for a record
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ClockIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid'
import { apiRequest } from '../../lib/api'
import { useTranslation } from '../../hooks/useTranslation'
import { useAuth } from '../../hooks/useAuth'
import { useUserLookup } from '../../hooks/useUsers'
import { Avatar } from '../ui/Avatar'

interface AuditLog {
  id: string
  entity: string
  entityId: string
  action: 'create' | 'update' | 'delete'
  userId: string
  timestamp: string
  oldValues: Record<string, unknown> | null
  newValues: Record<string, unknown> | null
  changedFields: string[] | null
}

interface AuditResponse {
  data: AuditLog[]
  total: number
  page: number
  limit: number
  totalPages: number
}

interface Props {
  entity: 'leads' | 'accounts' | 'contacts' | 'opportunities' | 'events' | 'notes'
  entityId: string
}

// Fields to exclude from display (internal fields)
const EXCLUDED_FIELDS = ['id', 'createdAt', 'updatedAt', 'ownerId']

export function AuditHistoryPanel({ entity, entityId }: Props) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { getUser } = useUserLookup()
  const [expanded, setExpanded] = useState(false)
  const [expandedLogs, setExpandedLogs] = useState<Set<string>>(new Set())

  // Only admins can view audit logs
  const canView = user?.role === 'admin'

  // Fetch audit logs
  const { data: auditData, isLoading } = useQuery({
    queryKey: ['audit', entity, entityId],
    queryFn: () => apiRequest<AuditResponse>(`/audit/${entity}/${entityId}?limit=20`),
    enabled: canView,
  })

  if (!canView) {
    return null
  }

  const logs = auditData?.data || []
  const total = auditData?.total || 0

  const toggleLogExpanded = (logId: string) => {
    setExpandedLogs(prev => {
      const next = new Set(prev)
      if (next.has(logId)) {
        next.delete(logId)
      } else {
        next.add(logId)
      }
      return next
    })
  }

  const getActionLabel = (action: string) => {
    switch (action) {
      case 'create':
        return t('audit.created')
      case 'update':
        return t('audit.updated')
      case 'delete':
        return t('audit.deleted')
      default:
        return action
    }
  }

  const getActionColor = (action: string) => {
    switch (action) {
      case 'create':
        return 'bg-green-100 text-green-700'
      case 'update':
        return 'bg-blue-100 text-blue-700'
      case 'delete':
        return 'bg-red-100 text-red-700'
      default:
        return 'bg-gray-100 text-gray-700'
    }
  }

  const formatValue = (value: unknown): string => {
    if (value === null || value === undefined) return '-'
    if (typeof value === 'boolean') return value ? 'Yes' : 'No'
    if (typeof value === 'object') return JSON.stringify(value)
    return String(value)
  }

  const formatFieldName = (field: string): string => {
    // Convert camelCase to Title Case with spaces
    return field
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim()
  }

  const renderChanges = (log: AuditLog) => {
    if (log.action === 'create') {
      // Show initial values for create
      const values = log.newValues || {}
      const fields = Object.entries(values).filter(
        ([key]) => !EXCLUDED_FIELDS.includes(key) && values[key] !== null && values[key] !== ''
      )

      if (fields.length === 0) return null

      return (
        <div className="mt-2 space-y-1">
          {fields.map(([key, value]) => (
            <div key={key} className="text-xs">
              <span className="font-medium text-gray-600">{formatFieldName(key)}:</span>{' '}
              <span className="text-gray-900">{formatValue(value)}</span>
            </div>
          ))}
        </div>
      )
    }

    if (log.action === 'update' && log.changedFields && log.changedFields.length > 0) {
      const changes = log.changedFields.filter(f => !EXCLUDED_FIELDS.includes(f))
      if (changes.length === 0) return null

      return (
        <div className="mt-2 space-y-1">
          {changes.map((field) => {
            const oldVal = log.oldValues?.[field]
            const newVal = log.newValues?.[field]
            return (
              <div key={field} className="text-xs">
                <span className="font-medium text-gray-600">{formatFieldName(field)}:</span>{' '}
                <span className="text-red-600 line-through">{formatValue(oldVal)}</span>
                {' → '}
                <span className="text-green-600">{formatValue(newVal)}</span>
              </div>
            )
          })}
        </div>
      )
    }

    return null
  }

  return (
    <div className="border-t border-gray-200 pt-6">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex items-center justify-between w-full text-left"
      >
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <ClockIcon className="h-4 w-4" />
          {t('audit.history')}
          {total > 0 && (
            <span className="text-xs font-normal text-gray-500">
              ({total} {total === 1 ? t('audit.change') : t('audit.changes')})
            </span>
          )}
        </h3>
        {expanded ? (
          <ChevronUpIcon className="h-5 w-5 text-gray-400" />
        ) : (
          <ChevronDownIcon className="h-5 w-5 text-gray-400" />
        )}
      </button>

      {expanded && (
        <div className="mt-4">
          {isLoading ? (
            <div className="text-sm text-gray-500">{t('common.loading')}</div>
          ) : logs.length === 0 ? (
            <p className="text-xs text-gray-500">{t('audit.noHistory')}</p>
          ) : (
            <div className="space-y-3">
              {logs.map((log) => {
                const actor = getUser(log.userId)
                const isExpanded = expandedLogs.has(log.id)
                const hasDetails = log.action === 'create' || (log.changedFields && log.changedFields.length > 0)

                return (
                  <div
                    key={log.id}
                    className="p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <Avatar
                          firstName={actor?.firstName || '?'}
                          lastName={actor?.lastName}
                          size="xs"
                        />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {actor
                              ? actor.firstName && actor.lastName
                                ? `${actor.firstName} ${actor.lastName}`
                                : actor.email
                              : t('audit.unknownUser')}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ${getActionColor(log.action)}`}>
                              {getActionLabel(log.action)}
                            </span>
                            <span>
                              {new Date(log.timestamp).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </div>
                      {hasDetails && (
                        <button
                          type="button"
                          onClick={() => toggleLogExpanded(log.id)}
                          className="text-xs text-brand-600 hover:text-brand-700"
                        >
                          {isExpanded ? t('audit.hideDetails') : t('audit.showDetails')}
                        </button>
                      )}
                    </div>

                    {isExpanded && renderChanges(log)}
                  </div>
                )
              })}

              {total > logs.length && (
                <p className="text-xs text-center text-gray-500">
                  {t('audit.showingRecent', { shown: logs.length, total })}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
